import {Component, computed, EventEmitter, inject, Input, OnInit, Output, signal} from '@angular/core';
import {FormsModule} from "@angular/forms";
import {NgClass} from "@angular/common";
import {MatCheckbox} from "@angular/material/checkbox";
import {MatIcon} from "@angular/material/icon";
import {MatButton} from "@angular/material/button";
import {GameService} from "../../../services/game.service";
import {SnackbarService} from "../../../services/snackbar.service";
import {IGameSelection} from "../../../interfaces/IGameSelection";

@Component({
  selector: 'app-faq-game-selector',
  standalone: true,
  imports: [
    FormsModule,
    NgClass,
    MatCheckbox,
    MatIcon,
    MatButton
  ],
  templateUrl: './faq-game-selector.component.html',
  styleUrl: './faq-game-selector.component.css'
})
export class FaqGameSelectorComponent implements OnInit {
  private gameService = inject(GameService);
  private snackbarService = inject(SnackbarService);

  @Input() initialSelectedGameIds: string[] = [];
  @Output() selectedGameIdsChange = new EventEmitter<string[]>();

  public gameSelections = signal<IGameSelection[]>([]);
  public isLoading = signal(false);
  public searchTerm = signal('');

  public filteredGameSelections = computed(() => {
    const search = this.searchTerm().toLowerCase();
    if (!search) {
      return this.gameSelections();
    }
    return this.gameSelections().filter(gs =>
      gs.game.Name.toLowerCase().includes(search) ||
      gs.game.Subdomain.toLowerCase().includes(search)
    );
  });

  public selectedCount = computed(() => {
    return this.gameSelections().filter(gs => gs.selected).length;
  });

  public allFilteredSelected = computed(() => {
    const filtered = this.filteredGameSelections();
    if (filtered.length === 0) return false;
    return filtered.every(gs => gs.selected);
  });

  ngOnInit(): void {
    this.loadGames();
  }

  private loadGames(): void {
    this.isLoading.set(true);

    this.gameService.selectAllAvailableGames().subscribe({
      next: (games) => {
        const initialSet = new Set(this.initialSelectedGameIds);
        const selections: IGameSelection[] = games.map(game => ({
          game: game,
          selected: initialSet.has(game.Id)
        }));
        this.gameSelections.set(selections);
        this.isLoading.set(false);
      },
      error: (err) => {
        this.snackbarService.openErrorSnackBar('Failed to load games: ' + err.message);
        this.isLoading.set(false);
      }
    });
  }

  onSelectAll(): void {
    const filtered = this.filteredGameSelections();
    const allSelected = filtered.every(gs => gs.selected);

    this.gameSelections.update(selections =>
      selections.map(gs => {
        if (filtered.includes(gs)) {
          return {...gs, selected: !allSelected};
        }
        return gs;
      })
    );

    this.emitSelectedGameIds();
  }

  onGameSelectionChange(gameId: string, selected: boolean): void {
    this.gameSelections.update(selections =>
      selections.map(gs =>
        gs.game.Id === gameId ? {...gs, selected} : gs
      )
    );

    this.emitSelectedGameIds();
  }

  private emitSelectedGameIds(): void {
    const selectedIds = this.gameSelections()
      .filter(gs => gs.selected)
      .map(gs => gs.game.Id);
    this.selectedGameIdsChange.emit(selectedIds);
  }

  public getSelectedGameIds(): string[] {
    return this.gameSelections()
      .filter(gs => gs.selected)
      .map(gs => gs.game.Id);
  }
}
